import { ApiResult, HttpClient } from "../http.js";
import {
  ProjectTilbudslisteDto,
  TilbudslisteSectionInput,
} from "../types/project-tilbudsliste.js";

export interface CreateProjectTilbudslistePayload {
  sections?: TilbudslisteSectionInput[] | null;
  id?: string;
}

export interface ReplaceProjectTilbudslistePayload {
  sections?: TilbudslisteSectionInput[] | null;
}

/**
 * Per-project tilbudsliste (contract document). One per project, keyed by
 * `projectId`. NOT the org-level `TilbudslisterApi` template.
 *
 * `get` resolves to `null` when the project has no tilbudsliste yet (the API
 * returns 204 No Content).
 */
export class ProjectTilbudslisteApi {
  constructor(private readonly http: HttpClient) {}

  async get(projectId: string): Promise<ApiResult<ProjectTilbudslisteDto | null>> {
    const result = await this.http.get<ProjectTilbudslisteDto>(
      `/api/projects/${encodeURIComponent(projectId)}/tilbudsliste`,
    );
    if (result.ok && result.status === 204) {
      return { ok: true, status: 204, data: null };
    }
    return result;
  }

  create(
    projectId: string,
    payload: CreateProjectTilbudslistePayload = {},
  ): Promise<ApiResult<ProjectTilbudslisteDto>> {
    return this.http.post<ProjectTilbudslisteDto>(
      `/api/projects/${encodeURIComponent(projectId)}/tilbudsliste`,
      { body: payload },
    );
  }

  /** Whole-document replace of the section/position hierarchy. 409 if locked. */
  update(
    projectId: string,
    payload: ReplaceProjectTilbudslistePayload,
  ): Promise<ApiResult<ProjectTilbudslisteDto>> {
    return this.http.put<ProjectTilbudslisteDto>(
      `/api/projects/${encodeURIComponent(projectId)}/tilbudsliste`,
      { body: payload },
    );
  }

  lock(projectId: string): Promise<ApiResult<ProjectTilbudslisteDto>> {
    return this.http.post<ProjectTilbudslisteDto>(
      `/api/projects/${encodeURIComponent(projectId)}/tilbudsliste/lock`,
    );
  }

  unlock(projectId: string): Promise<ApiResult<ProjectTilbudslisteDto>> {
    return this.http.post<ProjectTilbudslisteDto>(
      `/api/projects/${encodeURIComponent(projectId)}/tilbudsliste/unlock`,
    );
  }
}
